import { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { NEWS_MAX_PAGES } from '../config/news';
import { fetchArticlesPage } from '../services/news';
import type { NewsArticle } from '../types';

/**
 * Fil paginé de l'onglet News.
 *
 * CACHE SUR LE MODÈLE DE `home_contents` : le dernier fil connu est relu au montage et s'affiche
 * sans attendre le réseau. Il n'a pas de durée de validité — un article de trois jours vaut mieux
 * qu'un écran vide dans le métro. Seule la PREMIÈRE page est conservée : garder les suivantes
 * ferait grossir le stockage sans rien apporter, personne ne rouvre l'application au milieu du fil.
 *
 * Contrairement à l'encart de l'accueil, l'ERREUR EST VISIBLE ICI. Là-bas le contenu était un
 * complément qui disparaissait en silence ; ici c'est l'objet même de l'onglet, et un écran vide
 * sans explication laisserait croire qu'il n'y a rien à lire.
 */
const STORAGE_KEY = 'news_feed';

/** Contenu éditorial PUBLIC : aucune donnée personnelle, rien à purger à la suppression de compte. */
export async function readCachedFeed(): Promise<NewsArticle[]> {
  try {
    const brut = await AsyncStorage.getItem(STORAGE_KEY);
    if (!brut) return [];
    const parsed: unknown = JSON.parse(brut);
    return Array.isArray(parsed) ? (parsed as NewsArticle[]) : [];
  } catch {
    // Cache illisible (format changé, écriture interrompue) : on repart de zéro, jamais d'erreur.
    return [];
  }
}

export async function writeCachedFeed(items: NewsArticle[]): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Rien retenu pour le prochain lancement : gênant hors ligne, jamais bloquant.
  }
}

/**
 * DÉDOUBLONNAGE PAR ID à l'insertion.
 *
 * Le curseur est résolu en INDEX côté serveur : une publication survenant pendant qu'on défile
 * décale tout le fil d'un rang, et la page suivante renvoie alors un article déjà affiché. Sans ce
 * filtre, React lèverait une clé dupliquée et le favori porterait sur deux lignes à la fois.
 *
 * Les articles déjà présents sont IGNORÉS, pas remplacés : remplacer réordonnerait la liste sous
 * les yeux de quelqu'un en train de lire.
 */
export function mergeUnique(existants: NewsArticle[], nouveaux: NewsArticle[]): NewsArticle[] {
  const vus = new Set(existants.map((a) => a.id));
  const ajouts = nouveaux.filter((a) => {
    if (vus.has(a.id)) return false;
    vus.add(a.id);
    return true;
  });
  return ajouts.length > 0 ? [...existants, ...ajouts] : existants;
}

export type FeedStatus = 'loading' | 'ready' | 'error';

export function useNewsFeed() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [status, setStatus] = useState<FeedStatus>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Il ne reste rien à charger : plus de curseur, plus de page, ou un garde-fou a fermé le robinet. */
  const [exhausted, setExhausted] = useState(false);

  /**
   * ÉTAT DE PAGINATION EN RÉFÉRENCES, pas en état React.
   *
   * Ces trois valeurs sont lues et écrites À L'INTÉRIEUR d'un même chargement. Passées par `useState`,
   * la garde « curseur identique au précédent » comparerait la valeur du rendu courant, pas celle
   * que la requête vient de recevoir : deux appels rapprochés se croiseraient et la garde ne verrait
   * jamais la répétition qu'elle est censée attraper.
   */
  const curseur = useRef<string | null>(null);
  const pages = useRef(0);
  /** Empêche deux chargements simultanés : le bas de liste peut se déclencher plusieurs fois. */
  const enCours = useRef(false);
  /**
   * MONTÉ OU NON. Une requête lancée juste avant que l'onglet quitte l'écran se résout après le
   * démontage : sans cette garde, on écrirait dans l'état d'un composant qui n'existe plus. React
   * ne s'en plaint plus en production, mais c'est du travail fait pour rien et, en test, une mise à
   * jour hors `act` que rien n'attend.
   */
  const monte = useRef(true);
  /**
   * Le réseau a-t-il déjà répondu ? Le cache et la requête partent ENSEMBLE (voir l'effet de
   * montage) : sans ce drapeau, un cache lu tardivement écraserait une page fraîche déjà affichée.
   */
  const reseauServi = useRef(false);

  const charger = useCallback(async (depuisLeDebut: boolean) => {
    if (enCours.current) return;
    enCours.current = true;

    if (depuisLeDebut) {
      curseur.current = null;
      pages.current = 0;
      setExhausted(false);
    }

    try {
      const precedent = curseur.current;
      const page = await fetchArticlesPage(precedent);
      if (!monte.current) return;
      pages.current += 1;

      reseauServi.current = true;
      setArticles((actuels) => {
        const base = depuisLeDebut ? [] : actuels;
        const fusion = mergeUnique(base, page.articles);
        // Le cache ne garde QUE la première page (cf. l'en-tête). Une réponse vide et valide
        // l'écrase : si tout a été dépublié, le fil doit se vider, pas ressusciter d'anciens
        // articles. C'est la différence entre « le site est injoignable » et « le site dit rien ».
        if (depuisLeDebut) void writeCachedFeed(fusion);
        return fusion;
      });

      // ─────────────────────────── GARDE-FOUS DE PAGINATION ───────────────────────────
      // Cinq conditions d'arrêt, toutes obligatoires même si l'API se comporte bien aujourd'hui.
      // Un curseur qui tournerait en rond côté serveur ferait défiler l'application jusqu'à
      // épuisement de la mémoire, sans qu'aucune erreur ne soit jamais levée.
      const suivant = page.nextCursor;
      const fini =
        !page.hasNextPage || //           l'API dit que c'est fini
        suivant === null || //            plus de curseur (absent ou explicitement null)
        suivant === precedent || //       curseur IDENTIQUE : la page suivante serait la même
        page.articles.length === 0 || //  page vide : rien ne viendra plus
        pages.current >= NEWS_MAX_PAGES; // plafond de sécurité

      curseur.current = fini ? null : suivant;
      setExhausted(fini);
      setStatus('ready');
    } catch {
      if (!monte.current) return;
      // On distingue « rien à montrer » de « on montre quelque chose de périmé » : tant qu'il reste
      // des articles à l'écran, une erreur de page suivante ne doit pas effacer ce qui est lu.
      setArticles((actuels) => {
        setStatus(actuels.length > 0 ? 'ready' : 'error');
        return actuels;
      });
      setExhausted(true);
    } finally {
      enCours.current = false;
      if (monte.current) {
        setRefreshing(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const chargerPlus = useCallback(() => {
    if (exhausted || loadingMore || refreshing || status !== 'ready') return;
    setLoadingMore(true);
    void charger(false);
  }, [charger, exhausted, loadingMore, refreshing, status]);

  const rafraichir = useCallback(() => {
    setRefreshing(true);
    void charger(true);
  }, [charger]);

  const reessayer = useCallback(() => {
    setStatus('loading');
    void charger(true);
  }, [charger]);

  useEffect(() => {
    monte.current = true;
    reseauServi.current = false;

    /**
     * LA REQUÊTE PART D'ABORD, SANS ATTENDRE LE DISQUE.
     *
     * L'ordre inverse — lire le cache, puis seulement appeler — retardait le réseau d'une lecture
     * AsyncStorage pour rien : les deux sont indépendants. Il avait aussi un effet de bord en test,
     * où la première écriture d'état tombait après un `await` et donc hors du `act` du rendu, ce
     * que React signale à juste titre.
     *
     * Le cache reste affiché EN PREMIER dans les faits — un disque répond avant un réseau — mais il
     * n'écrase jamais une page déjà reçue : c'est le rôle de `reseauServi`.
     */
    void charger(true);

    void (async () => {
      const caches = await readCachedFeed();
      if (!monte.current || reseauServi.current || caches.length === 0) return;
      setArticles(caches);
      setStatus('ready');
    })();

    return () => {
      monte.current = false;
    };
  }, [charger]);

  return { articles, status, refreshing, loadingMore, exhausted, chargerPlus, rafraichir, reessayer };
}
